import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ThemeProvider, alpha } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TvOutlinedIcon from '@mui/icons-material/TvOutlined';
import type { ComponentType } from 'react';
import BrandMark from '@/components/common/BrandMark';
import { useLocale } from '@/i18n/useLocale';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { createAppTheme } from '@/theme';
import type { ProcessConfigurationDto } from '@/types/configuration';
import OverviewSlide from './slides/OverviewSlide';
import InventorySlide from './slides/InventorySlide';
import StockAgingSlide from './slides/StockAgingSlide';
import ProcurementSlide from './slides/ProcurementSlide';
import InboundQualitySlide from './slides/InboundQualitySlide';
import OutboundSlide from './slides/OutboundSlide';
import ProductionSlide from './slides/ProductionSlide';
import JobWorkSlide from './slides/JobWorkSlide';
import WeightProductionSlide from './slides/WeightProductionSlide';
import AdjustmentsSlide from './slides/AdjustmentsSlide';
import ApprovalsSlide from './slides/ApprovalsSlide';
import BillingSlide from './slides/BillingSlide';

const SLIDE_DURATION_MS = 15_000;

// Identical configKey/hiddenWhenConfigKey shape and gating logic as Dashboard.tsx's CATEGORY_TABS
// (a third+ copy of the same small gate — see that file's comment on why this isn't extracted yet).
// stockAging has no Dashboard-tab counterpart and stays ungated, same as before.
interface SlideDef {
  key: string;
  Slide: ComponentType;
  configKey?: keyof ProcessConfigurationDto | (keyof ProcessConfigurationDto)[];
  hiddenWhenConfigKey?: keyof ProcessConfigurationDto | (keyof ProcessConfigurationDto)[];
}

const SLIDES: readonly SlideDef[] = [
  { key: 'overview', Slide: OverviewSlide },
  { key: 'inventory', Slide: InventorySlide },
  { key: 'stockAging', Slide: StockAgingSlide },
  {
    key: 'procurement', Slide: ProcurementSlide,
    configKey: 'purchaseOrderEnabled', hiddenWhenConfigKey: 'thirdPartyLogisticsEnabled',
  },
  { key: 'inboundQuality', Slide: InboundQualitySlide },
  { key: 'outbound', Slide: OutboundSlide, configKey: 'dispatchEnabled' },
  {
    key: 'production', Slide: ProductionSlide,
    configKey: 'productionEnabled', hiddenWhenConfigKey: 'thirdPartyLogisticsEnabled',
  },
  // Not hidden under 3PL, matching the Job Work nav and Dashboard tab: a 3PL warehouse can send a
  // client's goods out for processing.
  { key: 'jobWork', Slide: JobWorkSlide, configKey: 'jobWorkEnabled' },
  { key: 'weightProduction', Slide: WeightProductionSlide, configKey: 'weightProductionEnabled' },
  { key: 'adjustments', Slide: AdjustmentsSlide },
  { key: 'approvals', Slide: ApprovalsSlide, configKey: 'approvalWorkflowEnabled' },
  { key: 'billing', Slide: BillingSlide, configKey: ['thirdPartyLogisticsEnabled', 'billingEnabled'] },
];

/** Sits in the header, centered between the logo and the clock, and swaps with whichever slide is
 * currently active. */
function TvSlideTitle({ slideKey }: { slideKey: string }) {
  const { t } = useTranslation('dashboard');

  return (
    <Typography key={slideKey} sx={{ color: '#fff', fontWeight: 800, fontSize: '2.25rem', lineHeight: 1, whiteSpace: 'nowrap' }}>
      {t(`tabs.${slideKey}`)}
    </Typography>
  );
}

export default function TvDashboardPage() {
  const { t } = useTranslation('dashboard');
  const { direction } = useLocale();
  const navigate = useNavigate();
  const tvTheme = createAppTheme('dark', direction);
  const { data: processConfiguration } = useProcessConfiguration();

  const passesConfigGate = (slideDef: (typeof SLIDES)[number]) => {
    if (!('configKey' in slideDef) || !slideDef.configKey || !processConfiguration) return true;
    const keys = Array.isArray(slideDef.configKey) ? slideDef.configKey : [slideDef.configKey];
    return keys.every((key) => processConfiguration[key]);
  };
  const passesHiddenWhenGate = (slideDef: (typeof SLIDES)[number]) => {
    if (!('hiddenWhenConfigKey' in slideDef) || !slideDef.hiddenWhenConfigKey || !processConfiguration) return true;
    const keys = Array.isArray(slideDef.hiddenWhenConfigKey) ? slideDef.hiddenWhenConfigKey : [slideDef.hiddenWhenConfigKey];
    return !keys.some((key) => processConfiguration[key]);
  };
  const visibleSlides = SLIDES.filter((s) => passesConfigGate(s) && passesHiddenWhenGate(s));

  const [activeSlideRaw, setActiveSlide] = useState(0);
  // Clamped so a slide toggled off mid-session (config changed elsewhere) never indexes past the
  // end of the now-shorter visibleSlides array — same pattern as Dashboard.tsx's activeIndex.
  const activeSlide = Math.min(activeSlideRaw, visibleSlides.length - 1);
  const [now, setNow] = useState(() => dayjs());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [needsFullscreenPrompt, setNeedsFullscreenPrompt] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  // Auto-advance through slides forever — paused holds the current slide indefinitely instead of
  // advancing (the per-slide progress bar's CSS animation freezes in place via animationPlayState,
  // in sync with this timer being stopped). Also depends on activeSlide so a manual prev/next click
  // restarts the countdown for the newly-selected slide instead of the old timer firing moments
  // later and immediately overriding the manual navigation.
  useEffect(() => {
    if (isPaused) return;
    const id = setInterval(() => setActiveSlide((i) => (i + 1) % visibleSlides.length), SLIDE_DURATION_MS);
    return () => clearInterval(id);
  }, [isPaused, activeSlide, visibleSlides.length]);

  const goToPrevious = () => setActiveSlide((i) => (i - 1 + visibleSlides.length) % visibleSlides.length);
  const goToNext = () => setActiveSlide((i) => (i + 1) % visibleSlides.length);

  // Smooth-scroll the track to the active slide whenever it changes.
  useEffect(() => {
    trackRef.current?.scrollTo({ left: activeSlide * window.innerWidth, behavior: 'smooth' });
  }, [activeSlide]);

  // Live clock, ticking every second.
  useEffect(() => {
    const id = setInterval(() => setNow(dayjs()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // Browsers only grant fullscreen in direct response to a user gesture. Opening this page from
  // the header's "Open TV Display" button counts in some browsers (activation carries over into
  // the new tab) but not others — so we try automatically first, and if that's rejected, fall
  // back to a full-screen "tap to start" prompt whose click is a guaranteed-valid gesture.
  useEffect(() => {
    document.documentElement.requestFullscreen().catch(() => setNeedsFullscreenPrompt(true));
  }, []);

  const enterFullscreen = () => {
    document.documentElement
      .requestFullscreen()
      .then(() => setNeedsFullscreenPrompt(false))
      .catch(() => {});
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      enterFullscreen();
    }
  };

  return (
    <ThemeProvider theme={tvTheme}>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          overflow: 'hidden',
          bgcolor: '#0a0f1a',
          backgroundImage: 'radial-gradient(circle at 15% 20%, rgba(112,137,219,0.18), transparent 45%), radial-gradient(circle at 85% 80%, rgba(28,122,67,0.16), transparent 45%)',
          backgroundSize: '160% 160%',
          animation: 'tvBackgroundDrift 30s ease-in-out infinite',
          '@keyframes tvBackgroundDrift': {
            '0%': { backgroundPosition: '0% 0%' },
            '50%': { backgroundPosition: '100% 100%' },
            '100%': { backgroundPosition: '0% 0%' },
          },
        }}
      >
        {needsFullscreenPrompt && !isFullscreen ? (
          <Box
            onClick={enterFullscreen}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1300,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              bgcolor: alpha('#000000', 0.75),
              backdropFilter: 'blur(4px)',
              cursor: 'pointer',
            }}
          >
            <TvOutlinedIcon sx={{ fontSize: 72, color: 'primary.light' }} />
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.75rem' }}>{t('tvDashboard.tapToStart')}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.65)' }}>{t('tvDashboard.tapToStartHint')}</Typography>
          </Box>
        ) : undefined}

        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 4, pt: 3, pb: 2 }}>
          <Box sx={{ width: 180, flexShrink: 0 }}>
            <BrandMark />
          </Box>
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0, px: 3 }}>
            <TvSlideTitle slideKey={visibleSlides[activeSlide]?.key ?? ''} />
          </Box>
          <Stack alignItems="flex-end" sx={{ flexShrink: 0 }}>
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '2rem', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {now.format('HH:mm:ss')}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>{now.format('dddd, DD MMMM YYYY')}</Typography>
          </Stack>
        </Stack>

        {/* Slide track */}
        <Box
          ref={trackRef}
          sx={{
            display: 'flex',
            width: '100vw',
            height: 'calc(100vh - 172px)',
            overflowX: 'hidden',
            scrollBehavior: 'smooth',
          }}
        >
          {visibleSlides.map(({ key, Slide }) => (
            <Box key={key} sx={{ flex: '0 0 100vw', width: '100vw', height: '100%', px: 4, boxSizing: 'border-box' }}>
              <Slide />
            </Box>
          ))}
        </Box>

        {/* Left/right manual navigation */}
        <Tooltip title={t('tvDashboard.previousSlide')}>
          <IconButton
            onClick={goToPrevious}
            sx={{
              position: 'fixed',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#fff',
              bgcolor: alpha('#000000', 0.35),
              opacity: 0.35,
              transition: 'opacity 0.2s ease',
              '&:hover': { opacity: 1, bgcolor: alpha('#000000', 0.55) },
            }}
          >
            <ChevronLeftIcon fontSize="large" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('tvDashboard.nextSlide')}>
          <IconButton
            onClick={goToNext}
            sx={{
              position: 'fixed',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#fff',
              bgcolor: alpha('#000000', 0.35),
              opacity: 0.35,
              transition: 'opacity 0.2s ease',
              '&:hover': { opacity: 1, bgcolor: alpha('#000000', 0.55) },
            }}
          >
            <ChevronRightIcon fontSize="large" />
          </IconButton>
        </Tooltip>

        {/* Slide indicators + progress */}
        <Stack direction="row" spacing={1.25} justifyContent="center" alignItems="center" sx={{ py: 2.5 }}>
          {visibleSlides.map((s, i) => (
            <Box key={s.key} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: i === activeSlide ? 44 : 8,
                  height: 8,
                  borderRadius: '4px',
                  bgcolor: i === activeSlide ? 'primary.light' : alpha('#ffffff', 0.25),
                  transition: 'width 0.3s ease, background-color 0.3s ease',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {i === activeSlide ? (
                  <Box
                    key={activeSlide}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: '#fff',
                      transformOrigin: 'left',
                      animation: `tvSlideProgress ${SLIDE_DURATION_MS}ms linear forwards`,
                      animationPlayState: isPaused ? 'paused' : 'running',
                      '@keyframes tvSlideProgress': {
                        from: { transform: 'scaleX(0)' },
                        to: { transform: 'scaleX(1)' },
                      },
                    }}
                  />
                ) : undefined}
              </Box>
              <Typography variant="caption" sx={{ color: i === activeSlide ? '#fff' : 'rgba(255,255,255,0.4)', fontWeight: 700 }}>
                {t(`tabs.${s.key}`)}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Corner controls */}
        <Stack direction="row" spacing={1} sx={{ position: 'fixed', bottom: 16, right: 16, opacity: 0.35, transition: 'opacity 0.2s ease', '&:hover': { opacity: 1 } }}>
          <Tooltip title={isPaused ? t('tvDashboard.resume') : t('tvDashboard.pause')}>
            <IconButton size="small" onClick={() => setIsPaused((v) => !v)} sx={{ color: '#fff', bgcolor: alpha('#000000', 0.35) }}>
              {isPaused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title={t('tvDashboard.toggleFullscreen')}>
            <IconButton size="small" onClick={toggleFullscreen} sx={{ color: '#fff', bgcolor: alpha('#000000', 0.35) }}>
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title={t('tvDashboard.exit')}>
            <IconButton size="small" onClick={() => navigate('/dashboard')} sx={{ color: '#fff', bgcolor: alpha('#000000', 0.35) }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </ThemeProvider>
  );
}
